'use client'

import { useGyymClient } from '@gyym/sdk-react'
import { Button, Modal, ModalProps, useSnackbar } from '@gyym/ui'
import { Close } from '@gyym/ui/icons'
import Image from 'next/image'
import React from 'react'

import { useAuthUser } from '@/modules/Auth/hooks/useAuthUser'
import { usePasskeyRegistration } from '@/modules/Passkey/hooks/usePasskeyRegistration'
import { isPasskeyCancelError } from '@/modules/Passkey/utils'

// Figma (GYYM-98) からエクスポートした Material Symbols のアイコン。
// 色（Primary/400 #00CCA3）は SVG 側に焼き込み済み。
const ICON_BASE = '/images/passkey'

type Benefit = {
  icon: string
  text: string
  note?: string
}

const BENEFITS: Benefit[] = [
  {
    icon: `${ICON_BASE}/encrypted.svg`,
    text: '不正ログイン防止やフィッシング対策で安全',
  },
  {
    icon: `${ICON_BASE}/lock.svg`,
    text: '生体認証などでログインが簡単',
    note: '※GYYMでは生体情報を取得・保存しません',
  },
  {
    icon: `${ICON_BASE}/bolt.svg`,
    text: '別端末でも再登録不要でログインが便利',
  },
]

/**
 * パスキー訴求の説明イラスト
 * Figma: 指紋（左上）→ 鍵（中央）→ 顔認証（右下）を破線で囲んだ構成
 */
const PasskeyIllustration = ({ height }: { height: number }) => (
  <div className="relative w-full overflow-hidden" style={{ height }}>
    {/* 中央の鍵アイコン */}
    <Image
      src={`${ICON_BASE}/key.svg`}
      alt=""
      width={78}
      height={78}
      unoptimized
      className="absolute left-1/2 top-[36px] -translate-x-1/2"
    />
    {/* 破線の囲み */}
    <div className="absolute left-1/2 top-[25px] h-[100px] w-[170px] -translate-x-1/2 rounded-[24px] border-2 border-dashed border-[#7AE3CD]" />
    {/* 左上：指紋 */}
    <Image
      src={`${ICON_BASE}/ellipse.svg`}
      alt=""
      width={80}
      height={80}
      unoptimized
      className="absolute left-[calc(50%-94.5px)] top-[8px] -translate-x-1/2"
    />
    <Image
      src={`${ICON_BASE}/fingerprint.svg`}
      alt=""
      width={56}
      height={56}
      unoptimized
      className="absolute left-[calc(50%-94.5px)] top-[20px] -translate-x-1/2"
    />
    {/* 右下：顔認証 */}
    <Image
      src={`${ICON_BASE}/ellipse.svg`}
      alt=""
      width={80}
      height={80}
      unoptimized
      className="absolute left-[calc(50%+95.5px)] top-[59px] -translate-x-1/2"
    />
    <Image
      src={`${ICON_BASE}/face.svg`}
      alt=""
      width={47}
      height={47}
      unoptimized
      className="absolute left-[calc(50%+96px)] top-[75px] -translate-x-1/2"
    />
  </div>
)

type Props = {
  /**
   * 表示コンテキスト
   * registration: 会員登録（メール認証）完了直後
   * login: ログイン直後の再訴求
   */
  context?: 'registration' | 'login'
} & Omit<ModalProps, 'title' | 'children'>

/**
 * パスキー登録訴求ダイアログ
 */
export const PasskeyPromotionModal = ({
  context = 'login',
  onClose,
  ...props
}: Props) => {
  const client = useGyymClient()
  const { revalidate } = useAuthUser()
  const { registerPasskey } = usePasskeyRegistration()
  const { openSnackbar } = useSnackbar()
  const [isProcessing, setIsProcessing] = React.useState(false)

  const handleRegister = async () => {
    setIsProcessing(true)
    try {
      await registerPasskey()

      openSnackbar({
        variant: 'success',
        message: 'パスキーを登録しました',
      })

      onClose?.()
    } catch (e) {
      // OSの認証画面でキャンセルした場合はダイアログを開いたままにする
      if (!isPasskeyCancelError(e)) {
        openSnackbar({
          variant: 'error',
          message: 'パスキーを登録できませんでした',
        })
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const handleHidePromotion = async () => {
    setIsProcessing(true)
    try {
      await client.auth.user.hidePasskeyPromotion()
      await revalidate()
      onClose?.()
    } catch {
      openSnackbar({
        variant: 'error',
        message: 'エラーが発生しました',
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Modal
      {...props}
      // ログイン時はヘッダー（タイトル）を持たず、本文右上に×ボタンのみを表示する（Figma準拠）
      title={
        context === 'registration' ? '会員登録が完了しました' : undefined
      }
      baseWidth={400}
      onClose={onClose}
    >
      <Modal.Body className="flex flex-col gap-4">
        {context === 'login' && (
          <button
            type="button"
            aria-label="閉じる"
            className="-mt-1 self-end text-gray-01"
            onClick={onClose}
          >
            <Close width="20px" height="20px" />
          </button>
        )}

        <div className="flex flex-col gap-2 text-gray-01">
          <h3 className="typography-headline3">
            パスキーでもっと簡単・安全にログインできます
          </h3>
          <p className="typography-caption1">
            パスキーとは、パスワードの代わりに、指紋認証や顔認証、スマートフォンの画面ロックを使ってアカウントの認証ができる仕組みです。
          </p>
        </div>

        <PasskeyIllustration height={context === 'registration' ? 139 : 147} />

        <ul className="flex flex-col gap-2 rounded-sm bg-[#EBFFFA] p-3">
          {BENEFITS.map((benefit) => (
            <li key={benefit.text} className="flex items-center gap-2">
              <Image
                src={benefit.icon}
                alt=""
                width={20}
                height={20}
                unoptimized
                className="size-5 shrink-0"
              />
              <span className="flex flex-1 flex-col">
                <span className="font-normal text-[#0F1413] typography-body2">
                  {benefit.text}
                </span>
                {benefit.note && (
                  <span className="text-[#0F1413]/60 typography-caption1">
                    {benefit.note}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={isProcessing}
          onClick={handleRegister}
        >
          <span className="flex flex-col items-center">
            <span>パスキーを設定する</span>
            <span className="typography-body2">
              デバイスの認証画面が表示されます
            </span>
          </span>
        </Button>

        <button
          type="button"
          className="flex flex-col items-center gap-1 self-stretch py-2.5 text-blue-01 disabled:text-gray-03"
          disabled={isProcessing}
          onClick={handleHidePromotion}
        >
          <span className="typography-button2">今後この案内を非表示にする</span>
          <span className="typography-caption1">
            マイページのSettingからも設定できます
          </span>
        </button>
      </Modal.Body>
    </Modal>
  )
}
